import * as React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { styled, theme } from '../../../../stitches.config'
import NavItemInterface from './NavItemInterface'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBriefcase } from '@fortawesome/free-solid-svg-icons/faBriefcase'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons/faUserPlus'
import { faRightToBracket } from '@fortawesome/free-solid-svg-icons/faRightToBracket'
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons/faCirclePlus'
import { faStore } from '@fortawesome/free-solid-svg-icons/faStore'

interface INavigationMenuDropdownItem extends NavItemInterface {
    image?: string
    gradient_words?: string
    icon_class?:
        | 'fa-briefcase'
        | 'fa-circle-plus'
        | 'fa-right-to-bracket'
        | 'fa-store'
        | 'fa-user-plus'
}

const gradientWordsStyles = {
    normal: {
        background: `-webkit-linear-gradient(83.53deg, ${theme.colors.primary} 18.47%, ${theme.colors.secondary} 78%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    hover: {
        background: theme.colors.secondaryDark,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
}

const ImageLink = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    cursor: 'pointer',
    py: '8px',
    pl: '$2',
    br: '$3',
    '.gradient-words': gradientWordsStyles.normal,
    '&:hover': {
        bc: '$lightest',
        '.gradient-words': gradientWordsStyles.hover,
    },
})

const ImageLinkImageWrapper = styled('div', {
    display: 'flex',
    alignItems: 'center',
})

const ImageLinkImage = styled(Image, {
    objectFit: 'contain',
    borderRadius: '3px',
})

const IconLinkWrapper = styled('div', {
    width: '3em',
    display: 'flex',
    alignItems: 'center',
    '& svg': {
        color: '$secondary',
        display: 'inline-block',
    },
})

const IconLink = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    cursor: 'pointer',
    height: '4em',
    br: '$3',
    '.gradient-words': gradientWordsStyles.normal,
    '&:hover': {
        bc: '$lightest',
        '.gradient-words': gradientWordsStyles.hover,
    },
})

const Icon = styled(FontAwesomeIcon, {
    '[data-state=open] &': {
        color: '$secondary',
        flexGrow: 1,
    },
    '@media (prefers-reduced-motion: no-preference)': {
        transition: 'transform 250ms ease',
    },
})

const NavLinkWrapperOuter = styled('div', {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 700,
    opacity: 1,
    color: '$darkest',
})
const NavLinkWrapperInner = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    pl: '1.1em',
    fontWeight: 700,
    opacity: 1,
    color: '$darkest',
})

const NavLink = styled('div', {
    fontSize: '1em',
    fontWeight: 700,
    opacity: 1,
    color: '$darkest',
    cursor: 'pointer',
    '&.view-all-link': {
        py: '25px',
        color: '$secondary',
        '&:hover': {
            color: '$secondaryDark',
        },
    },
    '&.simple-link': {
        py: '5px',
        width: '100%',
        '&:hover': {
            color: '$secondaryDark',
        },
    },
    '&:hover': {
        bc: 'transparent',
    },
})

const SubLabel = styled('div', {
    fontSize: '0.83em',
    fontWeight: 300,
    color: '$dark',
    mt: '5px',
})

const SectionHeader = styled('div', {
    color: '$mediumGray',
    textTransform: 'uppercase',
    fontSize: '0.9em',
    fontWeight: 300,
    mt: '$7',
    mb: '$5',
    '&:first-of-type': {
        mt: 'inherit',
    },
})

const replaceGradientWords = (text: string, gradientWords: string) => {
    const regex = new RegExp(`(${gradientWords})`, 'i')
    const textArray = text.split(regex)

    return textArray.map((str, index) => {
        if (regex.test(str)) {
            return (
                <span className={'gradient-words'} key={`s${index}`}>
                    {str}
                </span>
            )
        }
        return str
    })
}
interface INavLinkWithGradientText {
    linkText: string
    gradientWords: string | undefined
}
const NavLinkWithGradientText = ({
    linkText,
    gradientWords,
}: INavLinkWithGradientText) => {
    if (!gradientWords || linkText.indexOf(gradientWords) < 0) {
        return <NavLink>{linkText}</NavLink>
    }
    return <NavLink>{replaceGradientWords(linkText, gradientWords)}</NavLink>
}

export const NavigationMenuDropdownItem: React.FC<
    INavigationMenuDropdownItem
> = ({
    label,
    sub_label,
    url,
    image,
    gradient_words,
    test_id,
    is_view_all,
    icon_class,
}) => {
    if (!url) {
        return <SectionHeader>{label}</SectionHeader>
    }

    if (image) {
        return (
            <Link
                href={url ?? ''}
                key={url}
                passHref
                data-testid={test_id ?? ''}
            >
                <ImageLink>
                    <ImageLinkImageWrapper>
                        <ImageLinkImage
                            src={image}
                            alt={label}
                            width={100}
                            height={63}
                            data-testid={'image_link_image'}
                        ></ImageLinkImage>
                    </ImageLinkImageWrapper>
                    <NavLinkWrapperOuter>
                        <NavLinkWrapperInner>
                            {gradient_words ? (
                                <NavLinkWithGradientText
                                    linkText={label}
                                    gradientWords={gradient_words}
                                />
                            ) : (
                                <NavLink>{label}</NavLink>
                            )}
                            <SubLabel>{sub_label}</SubLabel>
                        </NavLinkWrapperInner>
                    </NavLinkWrapperOuter>
                </ImageLink>
            </Link>
        )
    }

    if (icon_class) {
        let iconClass
        switch (icon_class) {
            case 'fa-briefcase':
                iconClass = faBriefcase
                break
            case 'fa-circle-plus':
                iconClass = faCirclePlus
                break
            case 'fa-right-to-bracket':
                iconClass = faRightToBracket
                break
            case 'fa-user-plus':
                iconClass = faUserPlus
                break
            case 'fa-store':
                iconClass = faStore
                break
        }

        return (
            <Link
                href={url ?? ''}
                key={url}
                passHref
                data-testid={test_id ?? ''}
            >
                <IconLink>
                    <IconLinkWrapper>
                        {iconClass && (
                            <Icon
                                size={'2x'}
                                icon={iconClass}
                                data-testid={'icon_link_icon'}
                            />
                        )}
                    </IconLinkWrapper>
                    <NavLinkWrapperOuter>
                        <NavLinkWrapperInner>
                            {gradient_words ? (
                                <NavLinkWithGradientText
                                    linkText={label}
                                    gradientWords={gradient_words}
                                />
                            ) : (
                                <NavLink>{label}</NavLink>
                            )}
                            <SubLabel>{sub_label}</SubLabel>
                        </NavLinkWrapperInner>
                    </NavLinkWrapperOuter>
                </IconLink>
            </Link>
        )
    }

    return (
        <Link href={url ?? ''} key={url} passHref data-testid={test_id ?? ''}>
            <NavLinkWrapperOuter>
                <NavLinkWrapperInner>
                    <NavLink
                        className={
                            is_view_all ? 'view-all-link' : 'simple-link'
                        }
                        data-testid={'nav-link'}
                    >
                        {label}
                    </NavLink>
                    <SubLabel>{sub_label}</SubLabel>
                </NavLinkWrapperInner>
            </NavLinkWrapperOuter>
        </Link>
    )
}

export default NavigationMenuDropdownItem
